import React, { useState, useEffect } from "react";
import { Button, Modal } from "react-bootstrap";
import { Form } from "semantic-ui-react";
import useAuth from "../../../login/AuthContext";
import {
  CreateUnit,
  UnitCreationRequest,
} from "../../../../services/NetworkApi";
import {
  GetLifestylePortfolioTemplates,
  Portfolio,
  PortfolioLifestyleTemplate
} from "../../../../services/PortfolioApi";
import { ErrorResponse } from "../../../../services/Common";
import { UnitCreationPieces } from "./FormPieces";
import { useNetworkContext } from "../../../../contexts/NetworkContext/context";

interface CreateUnitModalProps {
  show: boolean;
  onClose: () => void;
  l1Id: number;
  l2Id: number;
  l2Name: string;
  portfolio?: Portfolio;
}

export const CreateUnitModal: React.FunctionComponent<CreateUnitModalProps> = ({
  show,
  onClose,
  l1Id,
  l2Id,
  l2Name,
  portfolio,
}) => {
  const [error, setError] = useState({ error: false, msg: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { getAuthToken } = useAuth();
  const { loadNetworkInfoForNetworks } = useNetworkContext();
  const [info, setInfo] = useState<UnitCreationRequest>({
    unitSize: undefined,
    unitTypeName: undefined,
    inSameRoom: false,
    templateId: undefined,
  });
  const [templates, setTemplates] = useState<PortfolioLifestyleTemplate[] | undefined>(undefined);

  const reset = () => {
    setInfo({...info, unitSize: undefined, unitTypeName: undefined, inSameRoom: false, templateId: undefined });
    setIsSubmitting(false);
    setError({ error: false, msg: "" });
  };

  const handleOnClose = () => {
    reset();
    onClose();
  };

  useEffect(() => {
    const load = async (pId: string) => {
      let templatesRequest = await GetLifestylePortfolioTemplates(pId, await getAuthToken());
      if (templatesRequest?.lifestyleTemplates) {
        setTemplates(templatesRequest?.lifestyleTemplates);
      }
    };

    const portfolioId = portfolio? portfolio.id : undefined;
    if (portfolioId && show) {
      load(portfolioId);
    }
  }, [portfolio, show]);

  const [errors, setErrors] = useState({
    unitTypeNameError: false,
    unitSizeError: false,
  });

  const validateFields = () => {
    const validationErrors = { ...errors };

    let errored = false;

    validationErrors.unitTypeNameError = info.unitTypeName == undefined;
    errored = errored || info.unitTypeName == undefined;

    validationErrors.unitSizeError =
      info.unitSize == undefined || info.unitSize < 1 || info.unitSize > 20;
    errored = errored || info.unitSize == undefined || info.unitSize < 1 || info.unitSize > 20;

    setErrors(validationErrors);
    return !errored;
  };

  const setValue = (value: string | boolean | number, field: string) => {
    setInfo({ ...info, [field]: value });
  };
  return (
    <Modal show={show} onHide={handleOnClose}>
      <Modal.Header closeButton>
        <Modal.Title>Create a unit type for {l2Name}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form
          error={error.error}
          onSubmit={async (event) => {
            event.preventDefault();

            if (isSubmitting) {
              return;
            }

            if (validateFields()) {
              setIsSubmitting(true);
              setError({ error: false, msg: "" });

              try {
                const createUnit = await CreateUnit(
                  l1Id,
                  l2Id,
                  info,
                  await getAuthToken()
                );

                if (!createUnit || createUnit instanceof ErrorResponse) {
                  setError({ msg: "something went wrong", error: true });
                  return;
                }

                await loadNetworkInfoForNetworks([l1Id]);
                reset();
                onClose();
              } catch (error) {
                setError({ msg: "something went wrong", error: true });
              } finally {
                setIsSubmitting(false);
              }
            }
          }}
        >
          <UnitCreationPieces
            unitTypeNameError={errors.unitTypeNameError}
            unitSizeError={errors.unitSizeError}
            templates={templates}
            setValue={setValue}
          />

          <Button
            style={{ marginTop: "5px" }}
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Adding..." : "Add"}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};
